<template>
  <v-layout column>
    <draggable
      v-model="sortableVariants"
      v-bind="dragOptions"
      ghost-class="bg-info"
      handle=".drag-handle"
    >
      <transition-group name="variants-list-transition">
        <div
          v-for="(variant, variantIndex) in sortableVariants"
          :key="variant.id + '-' + variantIndex"
          :class="{
            'mb-2': variantIndex < sortableVariants.length - 1,
            'my-2 px-1 pt-2 pb-1 elevation-4': showExtendedVariant,
            'pl-3': showExtendedVariant && sortableVariants.length === 1,
          }"
        >
          <v-layout>
            <!-- Drag Handle -->
            <v-scale-transition>
              <v-icon
                v-if="sortableVariants.length > 1"
                class="pl-1 pr-2"
                :class="{
                  'drag-handle clickable': showExtendedVariant,
                  'unselectable grey--text text--lighten-1':
                    !showExtendedVariant,
                }"
                :title="sortableVariants.length > 1 ? $t('reorder') : ''"
              >
                reorder
              </v-icon>
            </v-scale-transition>
            <!-- Fields across two rows -->
            <v-layout v-bind="layout">
              <v-layout
                column
                class="grow-2"
                :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
              >
                <!-- Description -->
                <v-text-field
                  v-if="allowExtended && variant.extended"
                  ref="variant-description-inputs"
                  v-model="variant.description"
                  :label="$t('menuItemVariant')"
                  clearable
                />
                <!-- Amount -->
                <v-combobox
                  ref="variant-amount-inputs"
                  v-model="variant.amount"
                  :items="uniqueAmounts"
                  :label="$t('menuItemAmount')"
                  :clearable="sortableVariants.length === 1"
                  :placeholder="$t('menuItemAmountPlaceholder')"
                />
              </v-layout>
              <v-layout
                column
                class="grow-1"
              >
                <!-- Additives -->
                <additives-select
                  v-if="allowExtended && variant.extended"
                  :menu-id="menuId"
                  :additives="menuAdditives"
                  :selection.sync="variant.additives"
                  @additive-created="
                    () => $store.dispatch('LIST_MENU_ADDITIVES', menuId)
                  "
                />
                <!-- Price with Modifier -->
                <v-layout
                  nowrap
                  align-center
                  :class="{
                    'non-default-modifier':
                      allowPriceModifiers && variant.modifier != '=',
                  }"
                >
                  <v-btn
                    v-if="allowPriceModifiers"
                    small
                    icon
                    class="ml-0 my-0 mr-1"
                    :title="$t('priceModifierChangeTooltip')"
                    @click.stop="onVariantModifierClicked(variant)"
                  >
                    <v-icon
                      class="modifier--icon"
                      :style="{
                        opacity: variant.modifier == '=' ? 0.2 : 1.0,
                        transform: variant.modifier == '=' ? 'scaleX(0.9)' : '',
                      }"
                    >
                      {{ variantModifierToIcon[variant.modifier] }}
                    </v-icon>
                  </v-btn>
                  <v-text-field
                    ref="variant-price-inputs"
                    v-model="variant.price"
                    data-cy="variant-price-input"
                    :label="$t(variantModifierToLabel[variant.modifier])"
                    append-icon="euro_symbol"
                    @change="() => onPriceChanged(variant)"
                  />
                </v-layout>
              </v-layout>
            </v-layout>
            <v-layout
              v-if="allowExtended || showDeleteButton"
              column
              style="max-width: 40px"
              class="ml-2"
            >
              <!-- Toggle extended Button -->
              <v-btn
                v-if="allowExtended"
                icon
                small
                flat
                color="grey"
                class="my-auto"
                :title="
                  !variant.extended
                    ? $t('menuItemVariantToggleExtendedOn')
                    : $t('menuItemVariantToggleExtendedOff')
                "
                @click="variant.extended = !variant.extended"
              >
                <v-icon
                  v-if="!variant.extended"
                  medium
                >
                  expand_more
                </v-icon>
                <v-icon
                  v-else
                  medium
                >
                  expand_less
                </v-icon>
              </v-btn>
              <!-- Delete Button -->
              <v-btn
                v-if="showDeleteButton"
                icon
                small
                flat
                color="grey"
                class="my-auto"
                :title="$t('delete')"
                :disabled="sortableVariants.length < 2"
                @click="confirmRemoveVariant(variantIndex)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </v-layout>
          </v-layout>
        </div>
      </transition-group>
    </draggable>
    <!-- Add more Variants Button-->
    <div
      v-if="allowMultiple && !maxVariantsReached"
      class="mr-auto mt-1 grey--text text--darken-1 pseudo-anchor unselectable"
      style="font-size: small"
      @click="addNewEmptyVariant()"
    >
      {{ $t('menuItemVariantAddAmount') }}
    </div>
  </v-layout>
</template>

<script>
import draggable from 'vuedraggable';

import AdditivesSelect from '@/components/Menu/AdditivesSelect';

const MAX_NUMBER_OF_VARIANTS = 6;

export default {
  components: {
    draggable,
    AdditivesSelect,
  },
  props: {
    /**
     * MenuItemVariant objects each with these fields:
     *
     *   These are considered 'extended-mode' fields:
     *
     *   - description (String)
     *   - additives (Array[Object])
     *
     *   While these are always shown/required:
     *
     *   - price (Number)
     *   - amount (String): Optional
     *
     * We assume these are working copies already, so we modify them
     * in-place here.
     */
    value: {
      type: Array[Object],
      required: true,
    },
    menuAdditives: {
      type: Array[Object],
      default: true,
    },
    menuId: {
      type: [Number, String],
      required: true,
    },
    /**
     * Whether a price can be defined as normal, additional, or rebate.
     */
    allowPriceModifiers: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether we show UI for description and additives fields.
     *
     * Otherwise we just show amount and price fields.
     */
    allowExtended: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether we show a button to create additional variants.
     */
    allowMultiple: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    variantModifierToIcon() {
      return {
        '=': 'drag_handle',
        '+': 'add',
        '-': 'remove',
      };
    },
    variantModifierToLabel() {
      return {
        '=': 'priceModifierNone',
        '+': 'priceModifierAdditional',
        '-': 'priceModifierRebate',
      };
    },
    showDeleteButton() {
      return this.sortableVariants.length > 1;
    },
    showExtendedVariant() {
      return (
        this.sortableVariants.length > 1 || this.sortableVariants[0].extended
      );
    },
    layout() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return {column: true};
      }
      return {row: true};
    },
    dragOptions() {
      return {
        animation: 150,
        disabled: false,
      };
    },
    sortableVariants: {
      get() {
        return this.value;
      },
      set(variants) {
        // Implement value prop and input signal for v-model support.
        this.$emit('input', variants);
      },
    },
    hasExtendedVariants() {
      return this.sortableVariants.some((variant) => !!variant.extended);
    },
    maxVariantsReached() {
      return this.sortableVariants.length >= MAX_NUMBER_OF_VARIANTS;
    },
    /**
     * Get unique amount values from $store and offer as presets.
     */
    uniqueAmounts() {
      let amounts = new Set();
      for (const menuItem of this.$store.state.common.menuItems) {
        for (const variant of menuItem.variants) {
          if (!variant.amount) {
            continue;
          }
          amounts.add(variant.amount);
        }
      }
      return Array.from(amounts).sort();
    },
  },
  methods: {
    onVariantModifierClicked(variant) {
      if (variant.modifier == '+') {
        variant.modifier = '-';
      } else if (variant.modifier == '-') {
        variant.modifier = '=';
      } else if (variant.modifier == '=') {
        variant.modifier = '+';
      }
    },
    /**
     * If focused and editing an input, ensure the value is assigned to v-model.
     */
    blurAllInputs() {
      const descriptionInputs = this.$refs['variant-description-inputs'];
      const amountInputs = this.$refs['variant-amount-inputs'];
      const priceInputs = this.$refs['variant-price-inputs'];

      if (!priceInputs) {
        return;
      }

      for (let i = 0; i < priceInputs.length; i++) {
        if (descriptionInputs && descriptionInputs[i]) {
          descriptionInputs[i].blur();
        }
        if (amountInputs && amountInputs[i]) {
          amountInputs[i].blur();
        }
        if (priceInputs && priceInputs[i]) {
          priceInputs[i].blur();
        }
      }
    },
    selectLastPriceInput() {
      const inputs = this.$refs['variant-price-inputs'];
      const lastInput = inputs[inputs.length - 1];
      if (!lastInput) {
        return;
      }
      lastInput.focus();
    },
    addNewEmptyVariant() {
      const shouldExtendAll = this.allowExtended && this.hasExtendedVariants;

      this.sortableVariants = this.sortableVariants.concat([
        {
          additives: [],
          amount: null,
          description: '',
          extended: this.allowExtended || this.hasExtendedVariants,
          modifier: '=',
          price: null,
        },
      ]);
      this.$nextTick(() => {
        this.selectLastPriceInput();
        if (shouldExtendAll) {
          for (const variant of this.sortableVariants) {
            variant.extended = true;
          }
        }
      });
    },
    /**
     * Handle typing invalid price and using comma VS dot as separator.
     *
     * Invalid prices are rejected and removed, valid prices are cast to
     * string using a dot as a cents separator.
     *
     * A price of 0.00 euros is considered valid, as there may be 'free'
     * products we still want to advertise.
     */
    onPriceChanged(variant) {
      if (typeof variant.price == 'string') {
        variant.price = variant.price.replace(',', '.').trim();
      }

      if (variant.price === '' || isNaN(variant.price)) {
        variant.price = null;
      } else {
        variant.price = Number(variant.price).toFixed(2);
      }
    },
    confirmRemoveVariant(index) {
      const variant = this.sortableVariants[index];
      if (!variant.price && !variant.amount) {
        // No need to confirm anything.
        this.removeVariant(index);
        return;
      }

      const confirmed = confirm(this.$t('menuItemVariantConfirmDeletion'));
      if (!confirmed) {
        return;
      }
      this.removeVariant(index);
    },
    removeVariant(index) {
      const copied = this.sortableVariants.slice();
      copied.splice(index, 1);
      this.sortableVariants = copied;
    },
  },
};
</script>

<style scoped>
.drag-handle {
  opacity: 0.66;
}
.drag-handle:hover {
  opacity: 1;
}

.non-default-modifier >>> .v-icon.modifier--icon,
.non-default-modifier >>> .v-text-field label {
  color: var(--color-info);
}

.variants-list-transition-enter-active,
.variants-list-transition-leave-active {
  transition: all 0.25s ease-in-out;
}
.variants-list-transition-enter,
.variants-list-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/components/Menu/EditMenuItemVariants.vue